import {
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { useMemo, useState } from "react";
import type {
	TwapHistoryItem,
	TwapSliceFill,
} from "#/hooks/use-hyperliquid-account";
import {
	TWAP_ACTIVE_GRID_CLASS,
	TWAP_GRID_CLASS,
} from "./constants/positions.constants.ts";
import { twapActiveColumns, twapColumns } from "./twap-columns.tsx";
import {
	TWAP_FILLS_GRID_CLASS,
	twapFillsColumns,
} from "./twap-fills-columns.tsx";
import type { TwapTableMeta } from "./types/positions.types.ts";
import { VirtualizedGridTable } from "./virtualized-grid-table.tsx";

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();

function isActiveTwap(item: TwapHistoryItem) {
	if (item.status.status !== "activated") return false;

	const startedAt = item.state.timestamp;
	const endsAt = startedAt + item.state.minutes * 60_000;
	if (Date.now() >= endsAt) return false;

	const totalSize = Number(item.state.sz);
	const executedSize = Number(item.state.executedSz);
	if (totalSize > 0 && executedSize >= totalSize) return false;

	return true;
}

type TwapSubTab = "active" | "history" | "fills";

type TwapTableProps = {
	twapHistory: TwapHistoryItem[];
	twapSliceFills: TwapSliceFill[];
	onCancelTwap: (coin: string, twapId: number) => void;
	isError?: boolean;
	onRetry?: () => void;
};

const subTabKeys: Array<{ key: TwapSubTab; labelKey: string }> = [
	{ key: "active", labelKey: "twap.sub-tab.active" },
	{ key: "history", labelKey: "twap.sub-tab.history" },
	{ key: "fills", labelKey: "twap.sub-tab.fills" },
];

export function TwapTable({
	twapHistory,
	twapSliceFills,
	onCancelTwap,
	isError,
	onRetry,
}: TwapTableProps) {
	const { t } = useTranslate();
	const [subTab, setSubTab] = useState<TwapSubTab>("active");

	// 같은 twapId로 terminated/finished 항목이 존재하면 제외
	const resolvedIds = useMemo(() => {
		const ids = new Set<number>();
		for (const item of twapHistory) {
			if (item.twapId != null && item.status.status !== "activated") {
				ids.add(item.twapId);
			}
		}
		return ids;
	}, [twapHistory]);

	const activeHistory = useMemo(
		() =>
			twapHistory.filter(
				(item) => isActiveTwap(item) && !resolvedIds.has(item.twapId ?? -1),
			),
		[twapHistory, resolvedIds],
	);

	const meta: TwapTableMeta = useMemo(
		() => ({ onCancel: onCancelTwap }),
		[onCancelTwap],
	);

	const activeTable = useReactTable({
		data: activeHistory,
		columns: twapActiveColumns,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
		meta,
	});

	const historyTable = useReactTable({
		data: twapHistory,
		columns: twapColumns,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	const fillsTable = useReactTable({
		data: twapSliceFills,
		columns: twapFillsColumns,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex items-center gap-1 border-b border-trade-border-subtle px-4 py-2">
				{subTabKeys.map((item) => (
					<button
						key={item.key}
						type="button"
						onClick={() => setSubTab(item.key)}
						className={`relative cursor-pointer px-2.5 py-1.5 text-caption1-medium transition-colors ${
							subTab === item.key
								? "text-trade-text-strong"
								: "text-trade-text-secondary hover:text-trade-text"
						}`}
					>
						{t(item.labelKey)}
						{subTab === item.key && (
							<span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
						)}
					</button>
				))}
			</div>
			{subTab === "fills" ? (
				<VirtualizedGridTable
					table={fillsTable}
					gridClass={TWAP_FILLS_GRID_CLASS}
					estimateSize={36}
					emptyMessage={t("positions.no-twap")}
					isError={isError}
					onRetry={onRetry}
				/>
			) : subTab === "active" ? (
				<VirtualizedGridTable
					table={activeTable}
					gridClass={TWAP_ACTIVE_GRID_CLASS}
					estimateSize={36}
					emptyMessage={t("positions.no-twap")}
					isError={isError}
					onRetry={onRetry}
				/>
			) : (
				<VirtualizedGridTable
					table={historyTable}
					gridClass={TWAP_GRID_CLASS}
					estimateSize={36}
					emptyMessage={t("positions.no-twap")}
					isError={isError}
					onRetry={onRetry}
				/>
			)}
		</div>
	);
}
